import type { PegaCustom, Element, Text } from 'slate';
import { marked } from 'marked';
import type { MarkedToken, Tokens } from 'marked';
import { parseFragment, serialize } from 'parse5';
import type * as parse5 from 'parse5';
import type { ComponentType } from 'react';

import { hasProp } from '@pega/cosmos-react-core';

import type { TokenMap, InteractionRenderers, RichTextViewerProps } from '../RichTextViewer.types';

import parseTextToCustomSlateNodes from './interactionRenderer';

type TableColumnAlignment = 'left' | 'center' | 'right';

type MarkedTokenToSlateNode = (
  token: MarkedToken,
  interactionRenderers?: InteractionRenderers[],
  markdownMap?: RichTextViewerProps['markdownMap'],
  htmlEnabled?: boolean
) => Element | Text;

const getTableColumnAlignment = (
  alignment: Tokens.Table['align'][number] | undefined
): TableColumnAlignment | undefined => {
  if (alignment === 'left' || alignment === 'center' || alignment === 'right') {
    return alignment;
  }

  return undefined;
};

const flattenInlineNodes = (nodes: (Element | Text)[]): (Element | Text)[] =>
  nodes.flatMap(node =>
    'type' in node && node.type === 'text' && 'children' in node
      ? flattenInlineNodes(node.children)
      : [node]
  );

const applyFormatting = (
  children: (Element | Text)[],
  token: MarkedToken,
  formatKey: 'italic' | 'bold' | 'code' | 'line-through'
): Element | Text => {
  if (children.length === 1 && 'type' in children[0] && children[0].type === 'custom') {
    return {
      ...children[0],
      [formatKey]: true
    };
  }

  if (children.length > 1) {
    const formattedChildren = flattenInlineNodes(children).map(child => ({
      ...child,
      [formatKey]: true
    }));

    return {
      type: 'text',
      children: formattedChildren
    };
  }

  const baseNode =
    children.length > 0 && !('type' in children[0])
      ? children[0]
      : { text: 'text' in token ? token.text || '' : '' };

  return {
    ...baseNode,
    [formatKey]: true
  };
};

type InlineHtmlFormatKey =
  | 'italic'
  | 'bold'
  | 'underline'
  | 'line-through'
  | 'superscript'
  | 'subscript';

// Limited set of inline HTML tags supported within markdown content.
const INLINE_HTML_TAG_FORMATS: Record<string, InlineHtmlFormatKey> = {
  em: 'italic',
  i: 'italic',
  strong: 'bold',
  b: 'bold',
  del: 'line-through',
  u: 'underline',
  sup: 'superscript',
  sub: 'subscript'
};

interface HtmlTagToken {
  name: string;
  isClosing: boolean;
  attributes: Record<string, string>;
}

const isParse5Element = (node: parse5.ChildNode | undefined): node is parse5.Element =>
  !!node && node.nodeName !== '#text' && node.nodeName !== '#comment';

const isParse5Text = (node: parse5.ChildNode): node is parse5.TextNode => node.nodeName === '#text';

const getParse5Attribute = (element: parse5.Element, name: string): string | undefined =>
  element.attrs.find(attr => attr.name === name)?.value;

// Concatenates the text of a parse5 node and all of its descendants.
const getParse5TextContent = (node: parse5.ChildNode): string => {
  if (isParse5Text(node)) return node.value;
  if (isParse5Element(node)) return node.childNodes.map(getParse5TextContent).join('');
  return '';
};

// Wraps text in a fenced markdown code block, using a fence long enough to not collide with any
// backtick run inside the content.
const toFencedCodeBlock = (code: string): string => {
  const longestBacktickRun = Math.max(0, ...(code.match(/`+/g) ?? []).map(run => run.length));
  const fence = '`'.repeat(Math.max(3, longestBacktickRun + 1));
  return `${fence}\n${code}\n${fence}`;
};

// Block-level HTML tags that are unwrapped and treated like markdown paragraphs.
const PARAGRAPH_LIKE_TAGS: ReadonlySet<string> = new Set(['div', 'p']);

// Heading HTML tags mapped to the markdown heading prefix they are treated as.
const HEADING_TAG_PREFIXES: Record<string, string> = {
  h1: '#',
  h2: '##',
  h3: '###',
  h4: '####',
  h5: '#####',
  h6: '######'
};

// List container HTML tags.
const LIST_TAGS: ReadonlySet<string> = new Set(['ul', 'ol']);

const isListElement = (node: parse5.ChildNode): node is parse5.Element =>
  isParse5Element(node) && LIST_TAGS.has(node.nodeName);

// Converts an HTML list (<ul>/<ol>) to the equivalent markdown list, recursing into nested lists
// with increased indentation so the structure matches a hand-written markdown list.
const convertListToMarkdown = (list: parse5.Element, indent = ''): string => {
  const ordered = list.nodeName === 'ol';
  const start = ordered ? Number.parseInt(getParse5Attribute(list, 'start') ?? '', 10) : Number.NaN;
  let index = Number.isNaN(start) ? 1 : start;

  const lines: string[] = [];

  list.childNodes
    .filter((node): node is parse5.Element => isParse5Element(node) && node.nodeName === 'li')
    .forEach(item => {
      const marker = ordered ? `${index}.` : '-';
      const inlineChildren = item.childNodes.filter(child => !isListElement(child));
      const inline = serialize({ ...item, childNodes: inlineChildren }).trim();

      lines.push(`${indent}${marker} ${inline}`);

      item.childNodes.filter(isListElement).forEach(nested => {
        lines.push(convertListToMarkdown(nested, `${indent}${' '.repeat(marker.length + 1)}`));
      });

      index += 1;
    });

  return lines.join('\n');
};

// A <pre> is only treated as a code block when its single element child is a <code>.
const isPreCodeBlock = (el: parse5.Element): boolean => {
  if (el.nodeName !== 'pre') return false;
  const elementChildren = el.childNodes.filter(isParse5Element);
  return elementChildren.length === 1 && elementChildren[0].nodeName === 'code';
};

const isSupportedBlockElement = (el: parse5.Element): boolean =>
  PARAGRAPH_LIKE_TAGS.has(el.nodeName) ||
  el.nodeName in HEADING_TAG_PREFIXES ||
  el.nodeName === 'a' ||
  el.nodeName === 'img' ||
  LIST_TAGS.has(el.nodeName) ||
  isPreCodeBlock(el);

// Returns the markdown representation of each top-level block (<div>/<p> as paragraphs, <h1>-<h6>
// as `#`-prefixed headings, <a>/<img> as links/images, <ul>/<ol> as lists, <pre> as a code block)
// when the block consists solely of supported blocks (ignoring whitespace), or undefined otherwise.
// Used to treat those blocks like their markdown equivalents.
const getTopLevelBlockMarkdown = (raw: string): string[] | undefined => {
  const { childNodes } = parseFragment(raw.trim());
  const elements = childNodes.filter(isParse5Element);
  const hasStrayText = childNodes.some(node => isParse5Text(node) && node.value.trim() !== '');

  if (hasStrayText || elements.length === 0 || elements.some(el => !isSupportedBlockElement(el))) {
    return undefined;
  }

  return elements.map(el => {
    if (LIST_TAGS.has(el.nodeName)) {
      return convertListToMarkdown(el);
    }
    if (el.nodeName === 'pre') {
      return toFencedCodeBlock(getParse5TextContent(el));
    }
    const inner = serialize(el);
    if (el.nodeName === 'a') {
      return `[${inner}](${getParse5Attribute(el, 'href') ?? ''})`;
    }
    if (el.nodeName === 'img') {
      return `![${getParse5Attribute(el, 'alt') ?? ''}](${getParse5Attribute(el, 'src') ?? ''})`;
    }
    const headingPrefix = HEADING_TAG_PREFIXES[el.nodeName];
    return headingPrefix ? `${headingPrefix} ${inner}` : inner;
  });
};

const MARKED_TOKEN_TYPES = [
  'blockquote',
  'br',
  'code',
  'codespan',
  'def',
  'del',
  'em',
  'escape',
  'heading',
  'hr',
  'html',
  'image',
  'link',
  'list',
  'list_item',
  'paragraph',
  'space',
  'strong',
  'table',
  'text'
] as const satisfies readonly MarkedToken['type'][];
const MARKED_TOKEN_TYPE_SET: ReadonlySet<string> = new Set(MARKED_TOKEN_TYPES);

const isMarkedToken = (token: MarkedToken | Tokens.Generic): token is MarkedToken =>
  MARKED_TOKEN_TYPE_SET.has(token.type);

// Expands top-level blocks (<div>/<p> and <h1> - <h6>) into the token stream they represent as
// markdown, so a series of them behaves exactly like the equivalent blank-line-separated markdown
// paragraphs and headings.
const expandTopLevelBlockTokens = (tokens: MarkedToken[]): MarkedToken[] =>
  tokens.flatMap(token => {
    if (token.type === 'html' && token.block) {
      const blockMarkdown = getTopLevelBlockMarkdown(token.raw);
      if (blockMarkdown) {
        return expandTopLevelBlockTokens(
          marked.lexer(blockMarkdown.join('\n\n'), { gfm: true }).filter(isMarkedToken)
        );
      }
    }
    return [token];
  });

// Classifies a single HTML tag token, returning its lowercased tag name and whether it is a
// closing tag. Opening tags are parsed with a real HTML parser; closing tags carry no attributes,
// so their name is read directly and anything other than a bare `</name>` is rejected.
const parseHtmlTagToken = (raw: string): HtmlTagToken | undefined => {
  const trimmed = raw.trim();

  if (trimmed.startsWith('</')) {
    if (!trimmed.endsWith('>')) return undefined;
    const name = trimmed.slice(2, -1).trim().toLowerCase();
    return name && !/[\s>/]/.test(name) ? { name, isClosing: true, attributes: {} } : undefined;
  }

  const [element] = parseFragment(trimmed).childNodes;
  if (!isParse5Element(element)) return undefined;
  const attributes = Object.fromEntries(element.attrs.map(attr => [attr.name, attr.value]));
  return { name: element.nodeName, isClosing: false, attributes };
};

// Inline HTML tags handled beyond text formatting: <span> unwraps to text, <a> becomes a link,
// <img> becomes an image, and <code> becomes an inline code span.
const INLINE_HTML_STRUCTURAL_TAGS: ReadonlySet<string> = new Set(['span', 'a', 'img', 'code']);

const getSupportedInlineHtmlTag = (raw: string): HtmlTagToken | undefined => {
  const tag = parseHtmlTagToken(raw);
  if (!tag) return undefined;
  return tag.name in INLINE_HTML_TAG_FORMATS || INLINE_HTML_STRUCTURAL_TAGS.has(tag.name)
    ? tag
    : undefined;
};

const getInlineHtmlFormat = (tagName: string): InlineHtmlFormatKey | undefined =>
  tagName in INLINE_HTML_TAG_FORMATS ? INLINE_HTML_TAG_FORMATS[tagName] : undefined;

// Finds the index of the matching closing tag, accounting for nested tags of the same name.
const findInlineHtmlCloseIndex = (
  tokens: MarkedToken[],
  openIndex: number,
  tagName: string
): number => {
  let depth = 1;

  for (let i = openIndex + 1; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token.type === 'html') {
      const tag = getSupportedInlineHtmlTag(token.raw);

      if (tag?.name === tagName) {
        depth += tag.isClosing ? -1 : 1;
        if (depth === 0) {
          return i;
        }
      }
    }
  }

  return -1;
};

// Applies an inline HTML formatting mark to a node and all of its descendant text nodes, matching
// the leaf-level representation that regular markdown emphasis produces.
const applyInlineHtmlFormat = (
  node: Element | Text,
  formatKey: InlineHtmlFormatKey
): Element | Text => {
  if ('type' in node) {
    if (node.type === 'custom') {
      return { ...node, [formatKey]: true };
    }

    if (node.type === 'text') {
      return {
        ...node,
        children: node.children.map(child => applyInlineHtmlFormat(child, formatKey))
      };
    }

    return node;
  }

  return { ...node, [formatKey]: true };
};

// Concatenates the text of a node and all of its descendants.
const getNodeText = (node: Element | Text): string =>
  'children' in node ? node.children.map(getNodeText).join('') : node.text;

// Builds a link leaf from an <a> tag's attributes and its inner nodes, matching the representation
// of a markdown link (href plus plain text).
const createLinkNode = (
  attributes: Record<string, string>,
  innerNodes: (Element | Text)[]
): Text => ({
  href: attributes.href ?? '',
  text: innerNodes.map(getNodeText).join('')
});

// Builds an image element from an <img> tag's attributes, matching the representation of a markdown
// image.
const createImageNode = (attributes: Record<string, string>): PegaCustom.ImageElement => ({
  type: 'image',
  alt: attributes.alt ?? '',
  src: attributes.src ?? '',
  children: [{ text: '' }]
});

const TEXT_MARK_KEYS = [
  'bold',
  'italic',
  'line-through',
  'code',
  'underline',
  'superscript',
  'subscript'
] as const;

const isPlainLeaf = (node: Text): boolean => !node.href && !node.searchResult;

// Returns the single plain text leaf a node represents (either a bare leaf or a `text` element
// wrapping exactly one plain leaf), or undefined when the node cannot be merged with a neighbor.
const asSingleLeaf = (node: Element | Text): Text | undefined => {
  if (!('type' in node)) {
    return isPlainLeaf(node) ? node : undefined;
  }

  if (node.type === 'text' && node.children.length === 1) {
    const [child] = node.children;
    if (!('type' in child) && isPlainLeaf(child)) {
      return child;
    }
  }

  return undefined;
};

const haveSameMarks = (a: Text, b: Text): boolean =>
  TEXT_MARK_KEYS.every(key => !!a[key] === !!b[key]);

// Merges adjacent plain text runs that share the same marks so that, for example, nested same-tag
// emphasis collapses into a single formatted run (e.g. <strong><strong>foo</strong> bar</strong>
// renders identically to **foo bar**).
const mergeAdjacentTextNodes = (nodes: (Element | Text)[]): (Element | Text)[] => {
  const merged: (Element | Text)[] = [];

  nodes.forEach(node => {
    const leaf = asSingleLeaf(node);
    const prev = merged[merged.length - 1];
    const prevLeaf = prev ? asSingleLeaf(prev) : undefined;

    if (leaf && prevLeaf && haveSameMarks(prevLeaf, leaf)) {
      merged[merged.length - 1] = { ...prevLeaf, text: prevLeaf.text + leaf.text };
    } else {
      merged.push(node);
    }
  });

  return merged;
};

const getCustomComponentFromMarkdownMap = <K extends keyof TokenMap>(
  token: TokenMap[K],
  markdownMap?: RichTextViewerProps['markdownMap']
): ComponentType<{ token: TokenMap[K] }> | undefined => {
  return markdownMap?.[token.type]?.(token);
};

const markDownToSlate = (
  tokens: MarkedToken[],
  interactionRenderers: InteractionRenderers[] | undefined,
  markdownMap: RichTextViewerProps['markdownMap'] | undefined,
  convertTokenToSlateNode: MarkedTokenToSlateNode,
  htmlEnabled: boolean
): (Element | Text)[] => {
  const nodes: (Element | Text)[] = [];

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    const openTag =
      htmlEnabled && token.type === 'html' ? getSupportedInlineHtmlTag(token.raw) : undefined;

    if (openTag && openTag.name === 'img' && !openTag.isClosing) {
      nodes.push(createImageNode(openTag.attributes));
    } else {
      const closeIndex =
        openTag && !openTag.isClosing ? findInlineHtmlCloseIndex(tokens, i, openTag.name) : -1;

      if (openTag && closeIndex !== -1) {
        if (openTag.name === 'code') {
          // <code> is treated like a markdown codespan: its raw content becomes a code-marked leaf.
          const innerRaw = tokens
            .slice(i + 1, closeIndex)
            .map(inner => inner.raw)
            .join('');
          nodes.push({ text: innerRaw, code: true });
        } else {
          const innerNodes = markDownToSlate(
            tokens.slice(i + 1, closeIndex),
            interactionRenderers,
            markdownMap,
            convertTokenToSlateNode,
            htmlEnabled
          );

          if (openTag.name === 'a') {
            nodes.push(createLinkNode(openTag.attributes, innerNodes));
          } else {
            const formatKey = getInlineHtmlFormat(openTag.name);
            if (formatKey) {
              innerNodes.forEach(innerNode => {
                nodes.push(applyInlineHtmlFormat(innerNode, formatKey));
              });
            } else {
              // Unwrapped tags (e.g. <span>) contribute their content as plain inline nodes.
              nodes.push(...innerNodes);
            }
          }
        }

        i = closeIndex;
      } else {
        nodes.push(convertTokenToSlateNode(token, interactionRenderers, markdownMap, htmlEnabled));
      }
    }
  }

  return htmlEnabled ? mergeAdjacentTextNodes(nodes) : nodes;
};

const convertTableCellToSlateNode = (
  cell: Tokens.TableCell,
  convertTokenToSlateNode: MarkedTokenToSlateNode,
  interactionRenderers?: InteractionRenderers[],
  markdownMap?: RichTextViewerProps['markdownMap'],
  align?: TableColumnAlignment,
  isHeaderCell?: boolean,
  htmlEnabled = false
): PegaCustom.TableCellElement => {
  const children = cell.tokens?.length
    ? flattenInlineNodes(
        markDownToSlate(
          cell.tokens.filter(isMarkedToken),
          interactionRenderers,
          markdownMap,
          convertTokenToSlateNode,
          htmlEnabled
        )
      )
    : parseTextToCustomSlateNodes(cell.text || '', interactionRenderers);

  const tableCell: PegaCustom.TableCellElement = {
    type: 'table-cell',
    ...(align ? { align } : {}),
    ...(isHeaderCell ? { isHeaderCell: true } : {}),
    children: children.length > 0 ? children : [{ text: '' }]
  };

  return tableCell;
};

const convertTableRowsToSlateNodes = (
  rows: Tokens.TableCell[][],
  convertTokenToSlateNode: MarkedTokenToSlateNode,
  interactionRenderers?: InteractionRenderers[],
  markdownMap?: RichTextViewerProps['markdownMap'],
  alignments?: Tokens.Table['align'],
  isHeaderCell?: boolean,
  htmlEnabled = false
): PegaCustom.TableRowElement[] => {
  return rows.map(row => {
    return {
      type: 'table-row',
      children: row.map((cell, columnIndex) => {
        return convertTableCellToSlateNode(
          cell,
          convertTokenToSlateNode,
          interactionRenderers,
          markdownMap,
          getTableColumnAlignment(alignments?.[columnIndex]),
          isHeaderCell,
          htmlEnabled
        );
      })
    };
  });
};

export const getNode = (
  token: MarkedToken,
  interactionRenderers?: InteractionRenderers[],
  markdownMap?: RichTextViewerProps['markdownMap'],
  htmlEnabled = false
): Element | Text => {
  if (!hasProp(token, 'type') || token.type === 'def')
    return { type: 'paragraph', children: [{ text: '' }] };

  if (token.type === 'html') {
    if (parseHtmlTagToken(token.raw)?.name === 'br') {
      return { text: '\n' };
    }

    const parsed = parseTextToCustomSlateNodes(token.raw, interactionRenderers);
    if (parsed.length === 1) {
      return parsed[0];
    }
    return {
      type: 'paragraph',
      children: parsed
    };
  }

  let children: (Element | Text)[];

  if (hasProp(token, 'tokens')) {
    children = markDownToSlate(
      (token.tokens as MarkedToken[]) || [],
      interactionRenderers,
      markdownMap,
      getNode,
      htmlEnabled
    );
  } else if (hasProp(token, 'items')) {
    children = markDownToSlate(
      token.items,
      interactionRenderers,
      markdownMap,
      getNode,
      htmlEnabled
    );
  } else {
    let mdText = '';

    if (token.type === 'text') mdText = token.raw;
    else if (hasProp(token, 'text')) mdText = token.text;

    children = parseTextToCustomSlateNodes(mdText, interactionRenderers);
  }

  if (markdownMap?.[token.type]) {
    const CustomComponent = getCustomComponentFromMarkdownMap(token, markdownMap);
    if (CustomComponent)
      return {
        type: 'override',
        component: <CustomComponent token={token} />,
        children: [{ text: '' }]
      };
  }

  if (!children.length) {
    return {
      type: 'paragraph',
      children: [{ text: token.raw }]
    };
  }

  switch (token.type) {
    case 'paragraph': {
      return {
        type: token.type,
        children
      };
    }
    case 'heading': {
      return {
        type:
          token.depth && token.depth <= 4
            ? (`heading-${token.depth}` as `heading-${1 | 2 | 3 | 4}`)
            : 'heading-4',
        children
      };
    }
    case 'link': {
      return {
        href: token.href,
        text: token.text || ''
      };
    }
    case 'em': {
      return applyFormatting(children, token, 'italic');
    }
    case 'strong': {
      return applyFormatting(children, token, 'bold');
    }
    case 'del': {
      return applyFormatting(children, token, 'line-through');
    }
    case 'codespan': {
      return applyFormatting(children, token, 'code');
    }
    case 'list': {
      if (token.ordered) {
        return {
          type: 'ordered-list',
          start: typeof token.start === 'number' ? token.start : 1,
          children: children as PegaCustom.ListItemElement[]
        };
      }

      return {
        type: 'unordered-list',
        children: children as PegaCustom.ListItemElement[]
      };
    }
    case 'list_item': {
      return {
        type: 'list-item',
        children: children as Element[]
      };
    }
    case 'blockquote': {
      return {
        type: 'block-quote',
        children
      };
    }
    case 'code': {
      return {
        type: 'code-block',
        language: token.lang,
        children: [{ text: token.text }]
      };
    }
    case 'table': {
      const headerRows = token.header?.length ? [token.header] : [];
      const bodyRows = token.rows || [];

      const headerSlateRows = convertTableRowsToSlateNodes(
        headerRows,
        getNode,
        interactionRenderers,
        markdownMap,
        token.align,
        true,
        htmlEnabled
      );

      const bodySlateRows = convertTableRowsToSlateNodes(
        bodyRows,
        getNode,
        interactionRenderers,
        markdownMap,
        token.align,
        false,
        htmlEnabled
      );

      const tableBody: PegaCustom.TableBodyElement = {
        type: 'table-body',
        children: bodySlateRows
      };

      const tableChildren:
        | [PegaCustom.TableHeadElement, PegaCustom.TableBodyElement]
        | [PegaCustom.TableBodyElement] =
        headerSlateRows.length > 0
          ? [{ type: 'table-head', children: headerSlateRows }, tableBody]
          : [tableBody];

      return {
        type: 'table',
        children: tableChildren
      };
    }
    case 'br': {
      return { text: '\n' };
    }
    case 'hr': {
      return {
        type: 'horizontal-rule',
        children: [{ text: '' }]
      };
    }
    case 'image': {
      return {
        type: 'image',
        alt: token.text,
        src: token.href,
        children: [{ text: '' }]
      };
    }
    default: {
      return {
        type: token.type,
        children
      } as Element;
    }
  }
};

export const convertMarkdownForEditor = (
  md = '',
  searchRenderers?: InteractionRenderers[]
): (Element | Text)[] => {
  return [
    {
      type: 'paragraph',
      children: parseTextToCustomSlateNodes(md, searchRenderers)
    }
  ];
};

// Converts markdown into Slate compatible nodes, also takes regex used
// in Feed Component to pass them down to custom elements
const convertMarkdown = (
  md: string,
  interactionRenderers?: InteractionRenderers[],
  markdownMap?: RichTextViewerProps['markdownMap'],
  htmlEnabled = false
): (Element | Text)[] => {
  const lexer = new marked.Lexer({ gfm: true });
  const output = lexer.lex(md) as MarkedToken[];
  const tokens = htmlEnabled ? expandTopLevelBlockTokens(output) : output;
  return markDownToSlate(tokens, interactionRenderers, markdownMap, getNode, htmlEnabled);
};

export default convertMarkdown;
